// ============================================================================
// ROLE SERVICE
// ============================================================================
// API calls for IAM Role management

import { get, post, put, del } from '../client/axiosClient';
import { roleEndpoints } from '../endpoints/iam.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  RoleDto,
  CreateRoleRequest,
  UpdateRoleRequest,
  RoleQueryParams,
} from '../types/iam.types';

class RoleService {
  async getAll(params?: RoleQueryParams): Promise<PaginatedResponse<RoleDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(roleEndpoints.list(), params)
      : roleEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<RoleDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<RoleDto>> {
    const response = await get<ApiResponse<RoleDto>>(roleEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateRoleRequest): Promise<ApiResponse<RoleDto>> {
    const response = await post<ApiResponse<RoleDto>>(roleEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateRoleRequest): Promise<ApiResponse<RoleDto>> {
    const response = await put<ApiResponse<RoleDto>>(roleEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(roleEndpoints.delete(id));
    return response.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const response = await get<ApiResponse<boolean>>(roleEndpoints.validateCode(code, excludeId));
    return response.data.data;
  }
}

export const roleService = new RoleService();
export default roleService;
